import React, { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Play, Pause, Volume2, VolumeX, Maximize2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { PRODUCT_TABS } from '../mock/mock';
import { listAllClients, photoUrl } from '../lib/adminApi';

const Products = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab = PRODUCT_TABS.some(p => p.id === tabParam) ? tabParam : 'export';
  const [active, setActive] = useState(initialTab);
  const [muted, setMuted] = useState(true);
  const [videoAspects, setVideoAspects] = useState({}); // { [tabId]: 'w / h' }
  const videoRefs = useRef({});
  const [adminClients, setAdminClients] = useState([]); // live client list per category

  // Load admin-uploaded clients (public GET). Re-fetch when Retails tab opens
  // so newly uploaded photos appear without a hard refresh.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listAllClients();
        if (!cancelled) setAdminClients(rows);
      } catch { /* graceful — API optional */ }
    })();
    return () => { cancelled = true; };
  }, [active]);

  const activeProduct = PRODUCT_TABS.find(p => p.id === active);
  const [projectLightbox, setProjectLightbox] = useState(null);
  const [articleCategory, setArticleCategory] = useState(null); // { name, articles }
  const [articleIndex, setArticleIndex] = useState(0);
  const [articleAspects, setArticleAspects] = useState({}); // { [idx]: 'w / h' }
  const [clientPicker, setClientPicker] = useState(null); // { categoryData, clients }

  // Map mock category name → admin category id
  const catIdForMock = (mockName) => {
    const map = {
      'Residential Projects': 'residential',
      'Events Projects': 'events',
      'Others Institutions': 'others',
      'Gifting Projects': 'gifting'
    };
    return map[mockName];
  };

  const openCategory = (catData) => {
    const adminCatId = catIdForMock(catData.name);
    const liveClients = adminCatId ? adminClients.filter(c => c.category === adminCatId) : [];
    if (liveClients.length > 0) {
      // Show sub-layer client picker (Reference set + each admin client)
      setClientPicker({ categoryData: catData, clients: liveClients });
    } else {
      // No admin clients — open reference articles directly
      setArticleCategory(catData);
      setArticleIndex(0);
    }
  };

  const openClientArticles = (client) => {
    setClientPicker(null);
    setArticleCategory({
      name: client.name,
      caption: `${clientPicker?.categoryData?.caption || ''} \u2014 ${client.photos.length} article${client.photos.length === 1 ? '' : 's'}`,
      articles: client.photos.map(p => ({
        image: photoUrl(p.url),
        title: p.original_filename?.replace(/\.[^/.]+$/, '') || client.name,
        caption: client.name
      }))
    });
    setArticleIndex(0);
  };

  const openReferenceArticles = () => {
    if (!clientPicker) return;
    setArticleCategory(clientPicker.categoryData);
    setClientPicker(null);
    setArticleIndex(0);
  };

  // ESC closes the project lightbox
  useEffect(() => {
    if (!projectLightbox) return;
    const onKey = (e) => { if (e.key === 'Escape') setProjectLightbox(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [projectLightbox]);

  // ESC + arrow keys for the articles lightbox
  useEffect(() => {
    if (!articleCategory) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setArticleCategory(null);
      if (e.key === 'ArrowRight') setArticleIndex(i => (i + 1) % articleCategory.articles.length);
      if (e.key === 'ArrowLeft')  setArticleIndex(i => (i - 1 + articleCategory.articles.length) % articleCategory.articles.length);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [articleCategory]);

  // Reset aspect map whenever a fresh category opens so we don't leak old values
  useEffect(() => {
    if (!articleCategory) setArticleAspects({});
  }, [articleCategory]);

  // Auto-advance articles every 2 seconds
  useEffect(() => {
    if (!articleCategory) return;
    const id = setInterval(() => {
      setArticleIndex(i => (i + 1) % articleCategory.articles.length);
    }, 2000);
    return () => clearInterval(id);
  }, [articleCategory]);

  // Sync tab param → state if user navigates via URL
  useEffect(() => {
    if (tabParam && tabParam !== active && PRODUCT_TABS.some(p => p.id === tabParam)) {
      setActive(tabParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);

  useEffect(() => {
    // Autoplay active video, pause others
    Object.entries(videoRefs.current).forEach(([id, vid]) => {
      if (!vid) return;
      if (id === active) {
        vid.play().catch(() => {});
      } else {
        vid.pause();
      }
    });
  }, [active]);

  return (
    <div>
      {/* Hero */}
      <section className="pt-24 pb-14 bg-[#f2ebde]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-5">Products</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#1a1614] leading-[1.05] max-w-4xl">
            Four areas of expertise. One <span className="italic text-[#a67c3d]">standard of making</span>.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#4a3f36] leading-relaxed">
            Across global supply, hospitality, retail &amp; institutional projects, and engineered wood flooring, RCD brings more than six decades of material knowledge to contemporary manufacturing requirements.
          </p>
        </div>
      </section>

      {/* Sub-tab Nav */}
      <div className="sticky top-24 md:top-28 z-30 bg-[#faf6f0]/95 backdrop-blur-md border-b border-[#e8dfd1]" data-testid="products-subnav">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-1 md:gap-2 overflow-x-auto py-4">
            <p className="text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#a67c3d] mr-4 md:mr-6 whitespace-nowrap">Products —</p>
            {PRODUCT_TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                className={`px-4 md:px-5 py-2 text-xs md:text-sm tracking-[0.14em] uppercase transition-all duration-300 whitespace-nowrap ${
                  active === t.id
                    ? 'text-[#1a1614] font-medium border-b border-[#a67c3d]'
                    : 'text-[#6b5847] hover:text-[#1a1614]'
                }`}
              >
                {t.tag}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Video showcase */}
      <section className="pt-10 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {/* Full-bleed video — frame adapts to the active video's natural rotation */}
          <div
            className="relative w-full mx-auto overflow-hidden bg-[#1a1614] mb-14 max-h-[82vh]"
            style={{ aspectRatio: videoAspects[active] || '16 / 9' }}
          >
            {PRODUCT_TABS.map(p => (
              <video
                key={p.id}
                ref={el => (videoRefs.current[p.id] = el)}
                src={p.video}
                autoPlay={p.id === active}
                loop
                muted={muted}
                playsInline
                preload="auto"
                onLoadedMetadata={e => {
                  const v = e.currentTarget;
                  if (v.videoWidth && v.videoHeight) {
                    setVideoAspects(prev => (
                      prev[p.id] ? prev : { ...prev, [p.id]: `${v.videoWidth} / ${v.videoHeight}` }
                    ));
                  }
                }}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ${
                  active === p.id ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
              />
            ))}

            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1614]/70 via-transparent to-transparent pointer-events-none" />

            {/* Overlay text */}
            <div className="absolute left-0 right-0 bottom-0 p-6 md:p-10 flex items-end justify-between gap-6">
              <div>
                <p className="text-xs tracking-[0.28em] uppercase text-[#d4b483] mb-2">{activeProduct.tagline}</p>
                <h2 className="font-serif text-3xl md:text-5xl text-[#faf6f0]">{activeProduct.name}</h2>
              </div>

              <button
                onClick={() => setMuted(m => !m)}
                aria-label={muted ? 'Unmute video' : 'Mute video'}
                className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-[#faf6f0]/10 hover:bg-[#faf6f0]/20 backdrop-blur text-[#faf6f0] border border-[#faf6f0]/30 transition-colors flex-shrink-0"
              >
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
            </div>
          </div>

          {/* Details */}
          <div key={active} className="grid grid-cols-1 lg:grid-cols-12 gap-12 rcd-fade-up">
            <div className="lg:col-span-7">
              <div className="flex items-baseline gap-4 mb-5">
                <span className="font-serif text-lg text-[#a67c3d]">
                  0{PRODUCT_TABS.findIndex(p => p.id === active) + 1} / 0{PRODUCT_TABS.length}
                </span>
                <span className="text-xs tracking-[0.28em] uppercase text-[#6b5847]">{activeProduct.tag}</span>
              </div>
              <p className="text-lg md:text-xl text-[#4a3f36] leading-relaxed mb-10 max-w-2xl">
                {activeProduct.description}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 max-w-2xl mb-10">
                {activeProduct.highlights.map(h => (
                  <div key={h} className="flex items-center gap-3 text-sm text-[#3a2f27] border-b border-[#e0d6c5] pb-3">
                    <span className="w-1.5 h-1.5 bg-[#a67c3d] rounded-full flex-shrink-0" />
                    {h}
                  </div>
                ))}
              </div>

              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#1a1614] text-[#faf6f0] hover:bg-[#a67c3d] transition-colors text-sm tracking-wide"
              >
                Enquire about {activeProduct.tag} <ArrowRight size={15} />
              </Link>
            </div>

            <aside className="lg:col-span-5 lg:pl-8 lg:border-l border-[#e0d6c5]">
              <p className="text-xs tracking-[0.28em] uppercase text-[#a67c3d] mb-6">Snapshot</p>
              <div className="space-y-8">
                {activeProduct.stats.map(s => (
                  <div key={s.l}>
                    <div className="font-serif text-4xl md:text-5xl text-[#1a1614] mb-1">{s.v}</div>
                    <div className="text-xs md:text-sm tracking-[0.2em] uppercase text-[#6b5847]">{s.l}</div>
                  </div>
                ))}
              </div>

              <div className="mt-10 pt-8 border-t border-[#e0d6c5]">
                <p className="text-sm text-[#4a3f36] leading-relaxed">
                  Every {activeProduct.tag.toLowerCase()} project is built in our Jaipur atelier by third-generation artisans, then finished, packed and shipped by our in-house team.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Materials / Categories — currently rendered for Global Export */}
      {activeProduct.categories && activeProduct.categories.length > 0 && (
        <section key={`categories-${active}`} className="py-20 md:py-24 bg-[#faf6f0] border-t border-[#e0d6c5]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-12 gap-3">
              <div>
                <p className="text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-2">
                  Furniture Working Categories
                </p>
                <h2 className="font-serif text-3xl md:text-5xl text-[#1a1614] leading-tight">
                  Materials we <span className="italic text-[#a67c3d]">work in.</span>
                </h2>
              </div>
              <p className="text-sm text-[#6b5847] max-w-md md:text-right">
                Every export container leaves our Jaitpura atelier as a mix of these four material streams.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {activeProduct.categories.map((cat, i) => (
                <div
                  key={cat.name}
                  data-testid={`export-category-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="group bg-[#faf6f0] border border-[#e0d6c5] hover:border-[#a67c3d] hover:shadow-2xl transition-all duration-500"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#1a1614]">
                    <img
                      src={cat.image}
                      alt={cat.name}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-[900ms]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a1614]/70 via-transparent to-transparent" />
                  </div>
                  <div className="p-5 md:p-6">
                    <h3 className="font-serif text-2xl md:text-3xl text-[#1a1614] leading-tight mb-3">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-[#4a3f36] leading-relaxed">
                      {cat.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Projects — only for tabs that have a projects reel (currently Hotels) */}
      {activeProduct.projects && activeProduct.projects.length > 0 && (
        <section key={`projects-${active}`} className="py-20 md:py-24 bg-[#faf6f0] border-t border-[#e0d6c5]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-12 gap-3">
              <div>
                <p className="text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-2">
                  Featured {activeProduct.tag} Projects
                </p>
                <h2 className="font-serif text-3xl md:text-5xl text-[#1a1614] leading-tight">
                  Recent installs, <span className="italic text-[#a67c3d]">on video.</span>
                </h2>
              </div>
              <p className="text-sm text-[#6b5847] max-w-md md:text-right">
                Each project has its own reel &mdash; click any tile to watch the full video.
              </p>
            </div>

            {/* Separate project cards — 6 videos distributed evenly: 2×3 on mobile, 3×2 on desktop */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-7">
              {activeProduct.projects.map((pr, i) => (
                <div
                  key={pr.video}
                  data-testid={`hotel-project-card-${i}`}
                  className="group bg-[#faf6f0] border border-[#e0d6c5] hover:border-[#a67c3d] hover:shadow-2xl transition-all duration-500"
                >
                  <button
                    onClick={() => setProjectLightbox(pr)}
                    aria-label={`Open ${pr.title} full video`}
                    className="relative w-full overflow-hidden bg-[#0d0b09] aspect-[9/16] block cursor-pointer"
                  >
                    <video
                      src={pr.video}
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      className="absolute inset-0 w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-[900ms]"
                    />
                    {/* Soft top & bottom vignette so caption sits cleanly against black letterbox */}
                    <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#0d0b09]/60 via-transparent to-[#0d0b09]/30" />

                    {/* Expand hint */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                      <div className="flex items-center gap-2 bg-[#faf6f0]/95 backdrop-blur px-4 py-2 text-[10px] tracking-[0.28em] uppercase text-[#1a1614]">
                        <Maximize2 size={13} /> Watch full video
                      </div>
                    </div>
                  </button>

                  {/* Project name band under the video */}
                  <div className="px-4 py-3.5 md:px-5 md:py-4">
                    <p className="text-[9px] md:text-[10px] tracking-[0.28em] uppercase text-[#a67c3d] mb-1.5">
                      {pr.location}
                    </p>
                    <h3 className="font-serif text-base md:text-lg text-[#1a1614] leading-tight">
                      {pr.title}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Project lightbox — full video with native controls */}
      {projectLightbox && (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center bg-[#0d0b09]/97 backdrop-blur-sm p-4 md:p-10 rcd-theatre-in"
          onClick={() => setProjectLightbox(null)}
          data-testid="hotel-project-lightbox"
        >
          <button
            onClick={() => setProjectLightbox(null)}
            aria-label="Close full video"
            className="absolute top-5 right-5 md:top-8 md:right-8 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-[#faf6f0]/10 hover:bg-[#faf6f0]/25 border border-[#faf6f0]/25 text-[#faf6f0] transition-colors z-10"
            data-testid="hotel-project-lightbox-close"
          >
            <X size={20} />
          </button>
          <div className="relative w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative w-full overflow-hidden bg-black shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)]" style={{ aspectRatio: '16 / 9' }}>
              <video
                src={projectLightbox.video}
                autoPlay
                controls
                playsInline
                className="absolute inset-0 w-full h-full object-contain bg-black"
              />
            </div>
            <div className="mt-5 text-center">
              <p className="text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#d4b483] mb-2">
                {projectLightbox.location}
              </p>
              <h3 className="font-serif text-xl md:text-3xl text-[#faf6f0] leading-tight">
                {projectLightbox.title}
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* Slideshow marquee — right-to-left product photos (Retails now shows 4 categories, Flooring shows individual pieces) */}
      {activeProduct.slideshow && activeProduct.slideshow.length > 0 && (
        <section key={`slideshow-${active}`} className="py-20 md:py-24 bg-[#faf6f0] border-t border-[#e0d6c5] overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 mb-10 md:mb-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
              <div>
                <p className="text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#a67c3d] mb-2">
                  Recent {activeProduct.tag} Pieces
                </p>
                <h2 className="font-serif text-3xl md:text-5xl text-[#1a1614] leading-tight">
                  {activeProduct.slideshow[0].articles ? (
                    <>Four project verticals <span className="italic text-[#a67c3d]">— tap to explore.</span></>
                  ) : (
                    <>Fixtures, seating &amp; joinery <span className="italic text-[#a67c3d]">from our projects.</span></>
                  )}
                </h2>
              </div>
              <p className="text-sm text-[#6b5847] max-w-md md:text-right">
                {activeProduct.slideshow[0].articles
                  ? 'Every category opens a curated set of articles from real client work.'
                  : 'Every piece here was made in our Jaipur atelier for a real client fit-out.'}
              </p>
            </div>
          </div>

          <div
            className="relative w-full"
            data-testid="retails-slideshow"
            style={{ maskImage: 'linear-gradient(to right, transparent 0, #000 6%, #000 94%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 6%, #000 94%, transparent 100%)' }}
          >
            <div className="flex gap-6 md:gap-8 rcd-slide-rtl" style={{ width: 'max-content' }}>
              {[...activeProduct.slideshow, ...activeProduct.slideshow].map((s, i) => {
                const isCategory = !!s.articles;
                const img = s.cover || s.image;
                const titleText = s.name || s.title;

                if (isCategory) {
                  const adminCatId = catIdForMock(s.name);
                  const liveCount = adminCatId ? adminClients.filter(c => c.category === adminCatId).length : 0;
                  return (
                    <button
                      key={`cat-${i}`}
                      type="button"
                      onClick={() => openCategory(s)}
                      data-testid={`retails-category-${(s.name || '').toLowerCase().replace(/\s+/g, '-')}`}
                      className="group relative flex-shrink-0 w-[260px] md:w-[340px] lg:w-[380px] aspect-[3/4] overflow-hidden bg-[#1a1614] shadow-lg text-left cursor-pointer"
                    >
                      <img
                        src={img}
                        alt={titleText}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[900ms]"
                      />
                      {/* Article count pill */}
                      <div className="absolute top-4 left-4 bg-[#faf6f0]/95 text-[#1a1614] text-[9px] md:text-[10px] tracking-[0.28em] uppercase px-2.5 py-1">
                        {liveCount > 0
                          ? `${liveCount} Client${liveCount === 1 ? '' : 's'} + ${s.articles.length}`
                          : `${s.articles.length} Articles`}
                      </div>
                      {/* Hover overlay hint */}
                      <div className="absolute inset-0 bg-[#1a1614]/0 group-hover:bg-[#1a1614]/25 transition-colors duration-500" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                        <div className="flex items-center gap-2 bg-[#faf6f0]/95 backdrop-blur px-4 py-2 text-[10px] tracking-[0.28em] uppercase text-[#1a1614]">
                          <Maximize2 size={13} /> View Articles
                        </div>
                      </div>
                      <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 bg-gradient-to-t from-[#1a1614]/95 via-[#1a1614]/45 to-transparent text-[#faf6f0]">
                        <p className="text-[9px] md:text-[10px] tracking-[0.28em] uppercase text-[#d4b483] mb-1">
                          {s.caption}
                        </p>
                        <h3 className="font-serif text-lg md:text-2xl leading-tight">{titleText}</h3>
                      </div>
                    </button>
                  );
                }

                return (
                  <figure
                    key={`piece-${i}`}
                    className="relative flex-shrink-0 w-[260px] md:w-[340px] lg:w-[380px] aspect-[3/4] overflow-hidden bg-[#1a1614] shadow-lg"
                  >
                    <img
                      src={img}
                      alt={titleText}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <figcaption className="absolute inset-x-0 bottom-0 p-4 md:p-5 bg-gradient-to-t from-[#1a1614]/85 via-[#1a1614]/25 to-transparent text-[#faf6f0]">
                      <p className="text-[9px] md:text-[10px] tracking-[0.28em] uppercase text-[#d4b483] mb-1">
                        {s.caption}
                      </p>
                      <h3 className="font-serif text-base md:text-xl leading-tight">{titleText}</h3>
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Client picker — appears when a category has admin-uploaded clients.
          Lets visitors choose a specific client's photo set OR the reference articles. */}
      {clientPicker && (
        <div
          className="fixed inset-0 z-[135] flex items-center justify-center bg-[#0d0b09]/95 backdrop-blur-sm p-4 md:p-10 rcd-theatre-in"
          onClick={() => setClientPicker(null)}
          data-testid="client-picker"
        >
          <button
            onClick={() => setClientPicker(null)}
            aria-label="Close picker"
            data-testid="client-picker-close"
            className="absolute top-5 right-5 md:top-8 md:right-8 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-[#faf6f0]/10 hover:bg-[#faf6f0]/25 border border-[#faf6f0]/25 text-[#faf6f0] transition-colors z-10"
          >
            <X size={20} />
          </button>
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <p className="text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#d4b483] text-center mb-3">
              {clientPicker.categoryData.name}
            </p>
            <h3 className="font-serif text-2xl md:text-4xl text-[#faf6f0] text-center mb-8 md:mb-10">
              Choose a <span className="italic text-[#d4b483]">client project</span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {/* Reference articles card */}
              <button
                onClick={openReferenceArticles}
                data-testid="client-picker-reference"
                className="group relative aspect-[4/5] overflow-hidden bg-[#1a1614] border border-[#d4b483]/30 hover:border-[#d4b483] transition-colors text-left"
              >
                <img
                  src={clientPicker.categoryData.cover}
                  alt="Reference"
                  className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1614]/95 via-[#1a1614]/40 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <p className="text-[9px] md:text-[10px] tracking-[0.28em] uppercase text-[#d4b483] mb-1">Reference Set</p>
                  <h4 className="font-serif text-base md:text-xl text-[#faf6f0] leading-tight">All Projects</h4>
                </div>
              </button>
              {clientPicker.clients.map(c => (
                <button
                  key={c.id}
                  onClick={() => openClientArticles(c)}
                  data-testid={`client-picker-${c.id}`}
                  className="group relative aspect-[4/5] overflow-hidden bg-[#1a1614] border border-[#faf6f0]/10 hover:border-[#d4b483] transition-colors text-left"
                >
                  {c.photos[0] ? (
                    <img
                      src={photoUrl(c.photos[0].url)}
                      alt={c.name}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[900ms]"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-[#8a7f6f] text-xs">
                      No photos
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a1614]/95 via-[#1a1614]/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <p className="text-[9px] md:text-[10px] tracking-[0.28em] uppercase text-[#d4b483] mb-1">
                      {c.photos.length} photo{c.photos.length === 1 ? '' : 's'}
                    </p>
                    <h4 className="font-serif text-base md:text-xl text-[#faf6f0] leading-tight">{c.name}</h4>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Article lightbox — auto-advances every 2s with manual prev / next */}
      {articleCategory && (
        <div
          className="fixed inset-0 z-[140] flex items-center justify-center bg-[#0d0b09]/97 backdrop-blur-sm p-4 md:p-10 rcd-theatre-in"
          onClick={() => setArticleCategory(null)}
          data-testid="articles-lightbox"
        >
          <button
            onClick={() => setArticleCategory(null)}
            aria-label="Close articles"
            className="absolute top-5 right-5 md:top-8 md:right-8 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-[#faf6f0]/10 hover:bg-[#faf6f0]/25 border border-[#faf6f0]/25 text-[#faf6f0] transition-colors z-10"
            data-testid="articles-lightbox-close"
          >
            <X size={20} />
          </button>

          <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <p className="text-[10px] md:text-xs tracking-[0.32em] uppercase text-[#d4b483] mb-3 text-center">
              {articleCategory.name} &middot; {articleIndex + 1} / {articleCategory.articles.length}
            </p>

            <div
              className="relative w-full mx-auto overflow-hidden bg-black shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)]"
              style={{
                aspectRatio: articleAspects[articleIndex] || '4 / 3',
                maxHeight: '78vh'
              }}
            >
              {articleCategory.articles.map((a, idx) => (
                <img
                  key={idx}
                  src={a.image}
                  alt={a.title}
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    if (img.naturalWidth && img.naturalHeight) {
                      setArticleAspects(prev => (
                        prev[idx] ? prev : { ...prev, [idx]: `${img.naturalWidth} / ${img.naturalHeight}` }
                      ));
                    }
                  }}
                  className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-[900ms] ${
                    idx === articleIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}

              {/* Prev / Next */}
              <button
                onClick={() => setArticleIndex(i => (i - 1 + articleCategory.articles.length) % articleCategory.articles.length)}
                aria-label="Previous article"
                className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center bg-[#faf6f0]/15 hover:bg-[#faf6f0]/30 border border-[#faf6f0]/25 text-[#faf6f0] transition-colors"
                data-testid="articles-prev"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                onClick={() => setArticleIndex(i => (i + 1) % articleCategory.articles.length)}
                aria-label="Next article"
                className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center bg-[#faf6f0]/15 hover:bg-[#faf6f0]/30 border border-[#faf6f0]/25 text-[#faf6f0] transition-colors"
                data-testid="articles-next"
              >
                <ChevronRight size={22} />
              </button>
            </div>

            <div className="mt-5 text-center">
              <h3 className="font-serif text-xl md:text-3xl text-[#faf6f0] leading-tight mb-1">
                {articleCategory.articles[articleIndex].title}
              </h3>
              <p className="text-xs md:text-sm tracking-wide text-[#c9bfae]">
                {articleCategory.articles[articleIndex].caption}
              </p>
            </div>

            {/* Progress dots */}
            <div className="flex justify-center gap-2 mt-5">
              {articleCategory.articles.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setArticleIndex(idx)}
                  aria-label={`Go to article ${idx + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    idx === articleIndex ? 'w-8 bg-[#d4b483]' : 'w-3 bg-[#faf6f0]/30 hover:bg-[#faf6f0]/60'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* All products grid */}
      <section className="py-20 bg-[#f2ebde]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
            <h2 className="font-serif text-3xl md:text-4xl text-[#1a1614]">Explore all four verticals</h2>
            <p className="text-sm text-[#6b5847]">Click any card to switch the showcase above.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PRODUCT_TABS.map(p => (
              <button
                key={p.id}
                onClick={() => {
                  setActive(p.id);
                  window.scrollTo({ top: 300, behavior: 'smooth' });
                }}
                className={`group text-left bg-white overflow-hidden border transition-all duration-500 ${
                  active === p.id ? 'border-[#a67c3d]' : 'border-transparent hover:border-[#a67c3d]/40'
                }`}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[#1a1614]">
                  <video
                    src={p.video}
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    onMouseEnter={e => e.target.play().catch(() => {})}
                    onMouseLeave={e => e.target.pause()}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 left-3 bg-[#faf6f0]/90 text-[10px] tracking-[0.24em] uppercase px-2.5 py-1 text-[#1a1614]">
                    {p.tag}
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-xl text-[#1a1614] mb-2">{p.name}</h3>
                  <p className="text-xs tracking-[0.2em] uppercase text-[#a67c3d]">{p.tagline}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#1a1614] text-[#faf6f0]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-serif text-4xl md:text-5xl mb-6">Something specific in mind?</h2>
          <p className="text-[#c9bfae] max-w-2xl mx-auto mb-10 text-lg">
            Share your drawings, moodboard or key count — we'll come back with a considered response, not a template.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#d4b483] text-[#1a1614] hover:bg-[#faf6f0] transition-colors font-medium">
            Request a Quote <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Products;
